import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Save, Settings as SettingsIcon, ShieldOff, Trash2, AlertTriangle, Bell, BellOff, CreditCard, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { isPushSupported, pushPermission, getCurrentSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush, syncSubscriptionWithServer } from '../lib/webPush';

const TOGGLES = [
  { key: 'discovery_visible', label: 'Discoverable in swipe', desc: 'Others can find you in Discover' },
  { key: 'show_online_status', label: 'Show online status', desc: 'Display when you were last active' },
  { key: 'show_distance', label: 'Show distance', desc: 'Show your distance to other users' },
  { key: 'show_verified_only', label: 'Show only verified profiles', desc: 'Hide unverified users in Discover' },
  { key: 'event_attendance_visible', label: 'Show my event RSVPs', desc: 'Other users can see what events you attend' },
];

const NOTIFICATION_TOGGLES = [
  { key: 'push_messages', label: 'Push — New messages' },
  { key: 'push_matches', label: 'Push — New matches' },
  { key: 'push_likes', label: 'Push — New likes' },
  { key: 'push_event_reminders', label: 'Push — Event reminders' },
  { key: 'email_matches', label: 'Email — New matches' },
  { key: 'email_messages', label: 'Email — New messages' },
  { key: 'email_receipts', label: 'Email — Premium payment receipts' },
];

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-[#2A9D8F]' : 'bg-[#F4F1DE]'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function loadAll() {
    try {
      const [s, b] = await Promise.all([
        api('/users/settings'),
        api('/blocks/list').catch(() => ({ blocked_users: [] })),
      ]);
      const defaults = {
        discovery_visible: true,
        show_online_status: true,
        show_distance: true,
        push_messages: true,
        push_matches: true,
        push_likes: true,
        push_event_reminders: true,
        event_attendance_visible: true,
        show_verified_only: false,
        email_matches: true,
        email_messages: false,
        email_receipts: true,
      };
      setSettings({ ...defaults, ...(s.settings || {}) });
      setBlocked(b.blocked_users || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  // ─── Browser push subscription state ───
  const [pushSupported] = useState(isPushSupported());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported) return;
    // If the browser has a stale subscription that never reached our server, re-post it.
    syncSubscriptionWithServer().then((s) => setPushSubscribed(!!s));
  }, [pushSupported]);

  async function handleEnablePush() {
    setPushBusy(true);
    try {
      await subscribeToPush();
      setPushSubscribed(true);
      toast.success('Push notifications enabled — you\'ll get pinged on new matches & messages.');
    } catch (err) {
      const msg = err.message || 'Failed';
      if (msg.includes('Permission denied')) toast.error('Browser blocked notifications. Re-enable in your browser settings.');
      else toast.error(msg);
    } finally {
      setPushBusy(false);
    }
  }

  async function handleDisablePush() {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setPushSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setPushBusy(false);
    }
  }

  async function handleTestPush() {
    setPushBusy(true);
    try {
      let res = await sendTestPush();
      if (res.sent === 0) {
        // Try to re-sync the browser's subscription with the server, then retry once.
        await syncSubscriptionWithServer();
        res = await sendTestPush();
      }
      if (res.sent === 0) {
        toast.error('No active subscription on the server. Disable then re-enable notifications on this device.');
      } else {
        toast.success(`Test notification sent to ${res.sent} device${res.sent > 1 ? 's' : ''} — check your system tray.`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send test');
    } finally {
      setPushBusy(false);
    }
  }

  function update(k, v) { setSettings({ ...settings, [k]: v }); }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {};
      [...TOGGLES, ...NOTIFICATION_TOGGLES].forEach(({ key }) => {
        payload[key] = !!settings[key];
      });
      await api('/users/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleUnblock(blockedId) {
    try {
      await api(`/blocks/${blockedId}`, { method: 'DELETE' });
      setBlocked((b) => b.filter((u) => u.id !== blockedId));
      toast.success('User unblocked');
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function handleDeleteAccount() {
    try {
      await api('/users/me', { method: 'DELETE' });
      toast.success('Account deleted');
      logout();
      window.location.href = '/';
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  // ── Stripe billing portal ──
  const [portalBusy, setPortalBusy] = useState(false);
  async function openBillingPortal() {
    setPortalBusy(true);
    try {
      const { url } = await api('/subscriptions/portal', {
        method: 'POST',
        body: JSON.stringify({ return_url: window.location.origin + '/app/settings' }),
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || 'Could not open billing portal');
      setPortalBusy(false);
    }
  }

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-2">
        <SettingsIcon className="w-6 h-6 text-[#C8553D]" />
        <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Settings
        </h1>
      </div>
      <p className="text-sm text-[#6D6875] mb-6">Manage privacy, notifications, blocked users, and your account.</p>

      {/* Privacy & Discovery */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="settings-privacy-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4">Privacy & Discovery</h2>
        <ul className="space-y-4">
          {TOGGLES.map(({ key, label, desc }) => (
            <li key={key} className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-bold text-sm text-[#2B2D42]">{label}</p>
                <p className="text-xs text-[#6D6875]">{desc}</p>
              </div>
              <Toggle checked={!!settings[key]} onChange={(v) => update(key, v)} testId={`settings-toggle-${key}`} />
            </li>
          ))}
        </ul>
      </section>

      {/* Browser push notifications (Web Push) */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="settings-webpush-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3 flex items-center gap-2">
          <Bell className="w-4 h-4" /> Browser notifications
        </h2>
        {!pushSupported ? (
          <p className="text-xs text-[#6D6875]" data-testid="webpush-unsupported">Your browser doesn't support push notifications. Try Chrome, Edge, or Firefox on desktop.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#6D6875]">Get pinged in real-time on new matches and messages — even when AfroConnect isn't open.</p>
            <div className="flex flex-wrap gap-2">
              {pushSubscribed ? (
                <>
                  <button onClick={handleDisablePush} disabled={pushBusy} data-testid="webpush-disable-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[#FFE5DD] text-[#C8553D] hover:bg-[#FFD4C2] disabled:opacity-60">
                    {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellOff className="w-4 h-4" />}
                    Disable on this device
                  </button>
                  <button onClick={handleTestPush} disabled={pushBusy} data-testid="webpush-test-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[#2A9D8F]/10 text-[#2A9D8F] hover:bg-[#2A9D8F]/20 disabled:opacity-60">
                    <Bell className="w-4 h-4" /> Send test
                  </button>
                </>
              ) : (
                <button onClick={handleEnablePush} disabled={pushBusy} data-testid="webpush-enable-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[#C8553D] text-white hover:bg-[#9E3A26] disabled:opacity-60">
                  {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  Enable on this device
                </button>
              )}
            </div>
            {pushPermission() === 'denied' && (
              <p className="text-xs text-[#E76F51]">⚠️ Notifications are blocked in your browser. Click the lock icon in your address bar → Allow notifications.</p>
            )}
          </div>
        )}
      </section>

      {/* Billing (Stripe Customer Portal) */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="settings-billing-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> Billing & Subscription
        </h2>
        {user?.is_premium ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E9C46A] to-[#E07A5F] flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" fill="white" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#2B2D42]">You're Premium</p>
                {user.premium_expires && (
                  <p className="text-xs text-[#6D6875]">
                    Renews / expires {new Date(user.premium_expires).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={openBillingPortal}
              disabled={portalBusy}
              data-testid="settings-billing-portal-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[#2B2D42] text-white hover:bg-[#1a1c2e] disabled:opacity-60"
            >
              {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Manage subscription
            </button>
            <p className="text-xs text-[#B5B5B5] mt-2">Update your card, view past invoices, or cancel — handled securely by Stripe.</p>
          </>
        ) : (
          <div data-testid="settings-billing-upgrade">
            <p className="text-sm text-[#6D6875] mb-3">You're on the free plan. Upgrade to unlock unlimited likes, priority discovery, and more.</p>
            <Link
              to="/app/premium"
              data-testid="settings-billing-upgrade-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[#C8553D] text-white hover:bg-[#9E3A26]"
            >
              <Crown className="w-4 h-4" />
              Upgrade to Premium
            </Link>
          </div>
        )}
      </section>

      {/* Notifications */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="settings-notifications-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4">Notifications</h2>
        <ul className="space-y-4">
          {NOTIFICATION_TOGGLES.map(({ key, label }) => (
            <li key={key} className="flex items-center justify-between gap-4">
              <p className="font-bold text-sm text-[#2B2D42]">{label}</p>
              <Toggle checked={!!settings[key]} onChange={(v) => update(key, v)} testId={`settings-toggle-${key}`} />
            </li>
          ))}
        </ul>
      </section>

      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="settings-save-button"
        className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30 mb-6"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Save Settings</>}
      </button>

      {/* Blocked Users */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="settings-blocked-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4 flex items-center gap-2">
          <ShieldOff className="w-4 h-4" /> Blocked Users ({blocked.length})
        </h2>
        {blocked.length === 0 ? (
          <p className="text-sm text-[#B5B5B5] italic" data-testid="settings-blocked-empty">You haven't blocked anyone.</p>
        ) : (
          <ul className="space-y-2" data-testid="settings-blocked-list">
            {blocked.map((u) => (
              <li key={u.id} className="flex items-center gap-3 p-3 bg-[#FFFCF9] rounded-xl border border-[#F4F1DE]" data-testid={`settings-blocked-row-${u.id}`}>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-[#FFE5DD] flex-shrink-0">
                  {u.photos?.[0] ? <img src={u.photos[0]} alt={u.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-[#C8553D]">{u.name?.charAt(0)}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#2B2D42] truncate">{u.name}</p>
                  <p className="text-xs text-[#6D6875] truncate">{u.city || '—'}</p>
                </div>
                <button
                  onClick={() => handleUnblock(u.id)}
                  data-testid={`settings-unblock-${u.id}`}
                  className="text-xs font-bold text-[#C8553D] hover:bg-[#FFE5DD] px-3 py-1.5 rounded-lg"
                >
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Danger zone */}
      <section className="bg-white border-2 border-[#FFE0DA] rounded-2xl p-5" data-testid="settings-danger-zone">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#C8553D] mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Danger Zone
        </h2>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            data-testid="settings-delete-account-button"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#C8553D] hover:bg-[#FFE0DA] px-4 py-2 rounded-lg"
          >
            <Trash2 className="w-4 h-4" /> Delete my account
          </button>
        ) : (
          <div className="space-y-3" data-testid="settings-confirm-delete">
            <p className="text-sm text-[#2B2D42]">
              Are you sure? This permanently deletes your profile, matches, messages, and saved listings. <strong>This cannot be undone.</strong>
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteAccount}
                data-testid="settings-confirm-delete-button"
                className="bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-4 py-2 rounded-lg text-sm"
              >
                Yes, delete forever
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                data-testid="settings-cancel-delete-button"
                className="bg-[#F4F1DE] text-[#2B2D42] font-bold px-4 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
